import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FaUser, FaKey, FaShoppingBag, FaEdit } from 'react-icons/fa';
import { listMyOrders } from '../actions/orderActions';
import Message from '../components/ui/Message';
import Loader from '../components/ui/Loader';
import api from '../utils/api';

const ProfilePage = () => {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  
  const dispatch = useDispatch();
  
  const auth = useSelector((state) => state.auth);
  const { user } = auth;
  
  const orderListMy = useSelector((state) => state.orderListMy);
  const { loading: loadingOrders, error: errorOrders, orders } = orderListMy;
  
  useEffect(() => {
    if (user) {
      setFullName(user.full_name || '');
      setEmail(user.email || '');
      setAddress(user.address || '');
    }
    
    if (activeTab === 'orders') {
      dispatch(listMyOrders());
    }
  }, [dispatch, user, activeTab]);
  
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    
    // Check if passwords match when trying to update password
    if (newPassword && newPassword !== confirmPassword) {
      setMessage('密码不匹配');
      return;
    }
    
    if (newPassword && !currentPassword) {
      setMessage('需要输入当前密码');
      return;
    }
    
    setLoading(true);
    setMessage(null);
    
    try {
      const updateData = {
        full_name: fullName,
        email,
        address,
      };
      
      // Only include password fields if user is trying to update password
      if (newPassword) {
        updateData.current_password = currentPassword;
        updateData.new_password = newPassword;
      }
      
      const { data } = await api.put('/users/me', updateData);
      
      setSuccess(true);
      setMessage('个人资料已更新');
      
      // Clear password fields
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      // Update local state with new data
      setFullName(data.user.full_name || '');
      setEmail(data.user.email || '');
      setAddress(data.user.address || '');
      
      // Set timeout to remove success message
      setTimeout(() => {
        setSuccess(false);
        setMessage(null);
      }, 3000);
    } catch (error) {
      setMessage(
        error.response && error.response.data.message
          ? error.response.data.message
          : '更新失败'
      );
    }
    
    setLoading(false);
  };
  
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
      date.getDate()
    ).padStart(2, '0')}`;
  };
  
  const getStatusClass = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'shipped':
        return 'bg-purple-100 text-purple-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return '待处理';
      case 'processing':
        return '处理中';
      case 'shipped':
        return '已发货';
      case 'delivered':
        return '已送达';
      case 'cancelled':
        return '已取消';
      default:
        return status;
    }
  };
  
  return (
    <div className="profile-page">
      <h1 className="text-3xl font-bold mb-6">我的账户</h1>
      
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="flex border-b">
          <button
            className={`px-6 py-4 text-center ${
              activeTab === 'profile'
                ? 'border-b-2 border-red-700 text-red-700 font-semibold'
                : 'text-gray-600 hover:text-red-700'
            }`}
            onClick={() => setActiveTab('profile')}
          >
            <FaUser className="inline mr-2" />
            个人资料
          </button>
          <button
            className={`px-6 py-4 text-center ${
              activeTab === 'orders'
                ? 'border-b-2 border-red-700 text-red-700 font-semibold'
                : 'text-gray-600 hover:text-red-700'
            }`}
            onClick={() => setActiveTab('orders')}
          >
            <FaShoppingBag className="inline mr-2" />
            我的订单
          </button>
          <button
            className={`px-6 py-4 text-center ${
              activeTab === 'password'
                ? 'border-b-2 border-red-700 text-red-700 font-semibold'
                : 'text-gray-600 hover:text-red-700'
            }`}
            onClick={() => setActiveTab('password')}
          >
            <FaKey className="inline mr-2" />
            修改密码
          </button>
        </div>
        
        <div className="p-6">
          {message && <Message variant={success ? 'success' : 'error'}>{message}</Message>}
          
          {activeTab === 'profile' && (
            <form onSubmit={handleUpdateProfile}>
              <div className="mb-4">
                <label className="block text-gray-700 mb-2">姓名</label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="w-full p-2 border rounded"
                  placeholder="请输入姓名"
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-gray-700 mb-2">手机号码</label>
                <input
                  type="tel"
                  value={user?.phone_number || ''}
                  className="w-full p-2 border rounded bg-gray-100"
                  disabled
                />
                <p className="text-sm text-gray-500 mt-1">手机号码无法修改</p>
              </div>
              
              <div className="mb-4">
                <label className="block text-gray-700 mb-2">电子邮箱</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full p-2 border rounded"
                  placeholder="请输入电子邮箱"
                />
              </div>
              
              <div className="mb-6">
                <label className="block text-gray-700 mb-2">收货地址</label>
                <textarea
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  className="w-full p-2 border rounded"
                  rows={3}
                  placeholder="请输入详细地址"
                />
              </div>
              
              <button
                type="submit"
                className="bg-red-700 text-white px-6 py-2 rounded hover:bg-red-800"
                disabled={loading}
              >
                {loading ? <Loader size="sm" /> : '保存更改'}
              </button>
            </form>
          )}
          
          {activeTab === 'password' && (
            <form onSubmit={handleUpdateProfile}>
              <div className="mb-4">
                <label className="block text-gray-700 mb-2">当前密码</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full p-2 border rounded"
                  required
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-gray-700 mb-2">新密码</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full p-2 border rounded"
                  required
                  minLength={6}
                />
              </div>
              
              <div className="mb-6">
                <label className="block text-gray-700 mb-2">确认新密码</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full p-2 border rounded"
                  required
                />
              </div>
              
              <button
                type="submit"
                className="bg-red-700 text-white px-6 py-2 rounded hover:bg-red-800"
                disabled={loading}
              >
                {loading ? <Loader size="sm" /> : '更新密码'}
              </button>
            </form>
          )}
          
          {activeTab === 'orders' && (
            <div>
              <h2 className="text-xl font-semibold mb-4">订单历史</h2>
              
              {loadingOrders ? (
                <Loader />
              ) : errorOrders ? (
                <Message variant="error">{errorOrders}</Message>
              ) : orders && orders.length === 0 ? (
                <Message>您还没有订单</Message>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="p-3 text-left">订单号</th>
                        <th className="p-3 text-left">日期</th>
                        <th className="p-3 text-right">金额</th>
                        <th className="p-3 text-center">状态</th>
                        <th className="p-3 text-center">操作</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((order) => (
                        <tr key={order.id} className="border-t">
                          <td className="p-3">#{order.id}</td>
                          <td className="p-3">{formatDate(order.created_at)}</td>
                          <td className="p-3 text-right">¥{order.total_amount.toFixed(2)}</td>
                          <td className="p-3 text-center">
                            <span
                              className={`px-2 py-1 rounded-full text-xs ${getStatusClass(
                                order.status
                              )}`}
                            >
                              {getStatusText(order.status)}
                            </span>
                          </td>
                          <td className="p-3 text-center">
                            <a
                              href={`/order/${order.id}`}
                              className="text-red-700 hover:underline inline-flex items-center"
                            >
                              <FaEdit className="mr-1" />
                              查看
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;